import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { ConsistencyHeatmap } from '../components/ConsistencyHeatmap';
import { LifeScoreRing } from '../components/LifeScoreRing';
import { MetricCard } from '../components/MetricCard';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/Button';
import { Card, SectionTitle } from '../components/ui/Card';
import { goals } from '../data/insights';
import { DISCIPLINE_DAYS, DISCIPLINE_MIN_SCORE } from '../data/levels';
import { currentUser } from '../data/user';
import { useLocale } from '../contexts/LocaleContext';
import { useProgress } from '../contexts/ProgressContext';
import type { DayRecord } from '../types';
import { average, buildHistory, perfectDays } from '../utils/history';
import { bandKey } from '../utils/progress';
function trailingStrongDays(records: DayRecord[], todayScore: number): number {
  if (todayScore < DISCIPLINE_MIN_SCORE) return 0;
  let count = 1;
  for (let i = records.length - 1; i >= 0; i--) {
    if (records[i].score >= DISCIPLINE_MIN_SCORE) count++;else break;
  }
  return count;
}
export function Stats() {
  const {
    t,
    tl,
    dayUnit
  } = useLocale();
  const navigate = useNavigate();
  const progress = useProgress();
  const history = useMemo(() => buildHistory(90, currentUser.streak), []);
  const withToday = useMemo(() => [...history, {
    date: new Date(),
    score: progress.todayScore
  }], [history, progress.todayScore]);
  const week = withToday.slice(-7);
  const recorded = withToday.filter(day => day.score > 0).length;
  const consistency = Math.round(recorded / withToday.length * 100);
  const disciplineDays = trailingStrongDays(history, progress.todayScore);
  const workouts = goals.find(goal => goal.id === 'workouts');
  const links = [{
    key: 'stats.heatmap.title',
    to: '/stats/heatmap'
  }, {
    key: 'stats.review.title',
    to: '/stats/revisao'
  }, {
    key: 'stats.insights.title',
    to: '/stats/humor'
  }, {
    key: 'stats.goals.title',
    to: '/stats/metas'
  }, {
    key: 'eng.prot.entry',
    to: '/stats/protecao'
  }, {
    key: 'eng.recap.entry',
    to: '/stats/retrospectiva'
  }, {
    key: 'eng.share.entry',
    to: '/stats/compartilhar'
  }, {
    key: 'eng.league.entry',
    to: '/stats/liga'
  }];
  return <div className="px-2 pb-2" data-id="element-3646" data-mp="26b321b96y3bx">
      <PageHeader title={t('stats.title')} subtitle={t('stats.subtitle')} data-id="element-3647" data-mp="26e7boo5fdr9z" />

      <Card tone="dark" className="rounded-panel px-2 py-3" data-id="element-3648" data-mp="1sjxyhy2pcqwb">
        <p className="bee-overline px-1 text-[9px] font-bold text-ondark" data-id="element-3649" data-mp="1xby1jze2jzx4">
          {t('home.lifescore.title')}
        </p>
        <div className="flex justify-center pt-1" data-id="element-3650" data-mp="1inl2uunlk2yx">
          <LifeScoreRing score={progress.todayScore} size={184} bandLabel={t(bandKey(progress.band))} tone="dark" data-id="element-3651" data-mp="1nlmljeh4209v" />
        </div>
        <Button variant="gold" fullWidth className="mt-2" onClick={() => navigate('/stats/lifescore')} data-id="element-3652" data-mp="19ny9mw50u3gy">
          {t('stats.lifescore.open')}
        </Button>
      </Card>

      <section className="mt-2 grid grid-cols-2 gap-2" data-id="element-3653" data-mp="q0bm4132w215">
        <MetricCard label={t('stats.streak')} value={progress.streak} unit={dayUnit(progress.streak)} data-id="element-3654" data-mp="q1msm8nwrnvy" />
        <MetricCard label={t('stats.avg30')} value={average(withToday.slice(-30))} unit="/100" data-id="element-3655" data-mp="zyhmge98l03w" />
        <MetricCard label={t('stats.perfect')} value={perfectDays(withToday)} unit={dayUnit(perfectDays(withToday))} accent data-id="element-3656" data-mp="1dwz886fl02j6" />
        <MetricCard label={t('stats.consistency')} value={consistency} unit="%" hint={t('stats.consistencyHint')} data-id="element-3657" data-mp="1nobzavtk2oo5" />
      </section>

      <Card className="mt-2 p-2" data-id="element-3658" data-mp="1woi8i5i812hi">
        <SectionTitle data-id="element-3659" data-mp="1wnql3hys2b2b">{t('stats.weekly.title')}</SectionTitle>
        <p className="-mt-1 mb-2 text-[11px] leading-snug text-muted" data-id="element-3660" data-mp="1rpferh7oyiou">
          {t('stats.weekly.question')}
        </p>
        <dl className="space-y-1" data-id="element-3661" data-mp="3m65qlh7v3q25">
          {[{
          label: t('stats.workouts'),
          value: `${workouts?.current ?? 0} / ${workouts?.target ?? 0}`
        }, {
          label: t('stats.weekly.strongDays'),
          value: `${week.filter(day => day.score >= 70).length} / 7`
        }, {
          label: t('stats.weekly.average'),
          value: `${average(week)} / 100`
        }].map(row => <div key={row.label} className="flex items-baseline justify-between gap-2 border-b border-sand-line pb-1 last:border-b-0" data-id="element-3662" data-mp="38530mtdqxvly">
              <dt className="text-[13px] text-deep" data-id="element-3663" data-mp="3h6axr84j0qeg">{row.label}</dt>
              <dd className="bee-num text-[13px] font-bold text-deep" data-id="element-3664" data-mp="3h7uc2t6mgrdl">{row.value}</dd>
            </div>)}
        </dl>
      </Card>

      <Card className="mt-2 p-2" data-id="element-3665" data-mp="wfslwluo1tgt">
        <SectionTitle action={<button type="button" onClick={() => navigate('/stats/heatmap')} className="text-[11px] font-medium text-gold-deep transition-colors duration-150 ease-brand hover:text-deep" data-id="element-3667" data-mp="16k907ashz2ii">
              {t('stats.openFull')}
            </button>} data-id="element-3666" data-mp="wf0mt11v1dls">
          {t('stats.heatmap.title')}
        </SectionTitle>
        <p className="-mt-1 mb-2 text-[11px] leading-snug text-muted" data-id="element-3668" data-mp="sqrh1r3q3rt0">
          {t('stats.heatmap.question')}
        </p>
        <ConsistencyHeatmap records={withToday} data-id="element-3669" data-mp="iuo5jumzegte" />
      </Card>

      <Card tone={disciplineDays >= DISCIPLINE_DAYS ? 'dark' : 'light'} className={`mt-2 p-2 ${disciplineDays >= DISCIPLINE_DAYS ? 'rounded-panel border-gold/50' : ''}`} data-id="element-3670" data-mp="it3c68cel3x4">
        <h2 className={`text-section ${disciplineDays >= DISCIPLINE_DAYS ? 'text-gold' : 'text-deep'}`} data-id="element-3671" data-mp="9w2qwakmrlxw">
          {t('stats.discipline.title')}
        </h2>
        <p className={`mt-[2px] text-[12px] leading-snug ${disciplineDays >= DISCIPLINE_DAYS ? 'text-ondark' : 'text-muted'}`} data-id="element-3672" data-mp="245wiysw15mbk">
          {t('stats.discipline.body', {
          n: disciplineDays
        })}
        </p>
        {disciplineDays < DISCIPLINE_DAYS && <div className="mt-2 h-[7px] w-full overflow-hidden rounded-pill bg-sand-deep" data-id="element-3673" data-mp="3onyqewx52d3n">
            <span className="block h-full rounded-pill bg-soft" style={{
          width: `${Math.min(100, disciplineDays / DISCIPLINE_DAYS * 100)}%`
        }} data-id="element-3674" data-mp="3srwr3c243vyt" />
          </div>}
      </Card>

      <section className="mt-3" data-id="element-3675" data-mp="bbop9xuth5e9">
        <SectionTitle data-id="element-3676" data-mp="ban8dcr4doom">{t('stats.details')}</SectionTitle>
        <ul className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-3677" data-mp="g9qfr3uko6zr">
          {links.map(link => <li key={link.to} className="border-b border-sand-line last:border-b-0" data-id="element-3678" data-mp="2w14h8w5rv05h">
              <button type="button" onClick={() => navigate(link.to)} className="flex min-h-[52px] w-full items-center gap-2 py-2 text-left" data-id="element-3679" data-mp="3110duac53pb0">
                <span className="flex-1 text-[13.5px] font-medium text-deep" data-id="element-3680" data-mp="2oimvnrkmfuw5">
                  {t(link.key)}
                </span>
                <ChevronRightIcon size={16} className="shrink-0 text-muted" aria-hidden="true" data-id="element-3681" data-mp="32h4gnhj56tad" />
              </button>
            </li>)}
        </ul>
      </section>

      <p className="mt-2 px-1 text-[11px] leading-snug text-muted" data-id="element-3682" data-mp="24u5pfmb7ydjv">
        {tl({
        'pt-BR': 'Todos os números vêm do seu Daily Check. Nenhum dado é estimado.',
        'en-US': 'Every number comes from your Daily Check. Nothing is estimated.'
      })}
      </p>
    </div>;
}